import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useAuth } from "../../contexts/AuthContext";
import ReportPeriodFilter from "./ReportPeriodFilter";
import OverviewView from "./views/OverviewView";
import ClientsView from "./views/ClientsView";
import LeadsView from "./views/LeadsView";
import EnquiriesView from "./views/EnquiriesView";
import OperationsView from "./views/OperationsView";
import CompletedOperationsView from "./views/CompletedOperationsView";
import WarehouseView from "./views/WarehouseView";
import { ReportContext } from "./ReportKit";
import { printElement } from "./printReport";
import {
  REPORT_PERIODS,
  collectOptions,
  describeCompare,
  describePeriod,
  formatDate,
  getPeriodDates,
} from "./reportUtils";
import "./Reports.scss";

const SALES = ["ROLE_SALES-MANAGER"];

// `filters` are the query-parameter names each backend report accepts.
const REPORTS = {
  overview: {
    title: "Sales overview",
    icon: "mdi:view-dashboard-outline",
    roles: SALES,
    View: OverviewView,
    description:
      "A quick look at your sales: clients, leads, enquiries and money.",
    filters: ["ownerId", "source", "industry"],
    compare: true,
    granularity: true,
  },
  client: {
    title: "Clients",
    icon: "mdi:account-group-outline",
    roles: SALES,
    View: ClientsView,
    description:
      "Who your clients are, who looks after them, and which ones are new.",
    filters: ["ownerId", "source", "industry"],
    compare: true,
    granularity: true,
  },
  lead: {
    title: "Leads",
    icon: "mdi:bullseye-arrow",
    roles: SALES,
    View: LeadsView,
    description: "People who might buy, and how many of them became clients.",
    filters: ["ownerId", "source", "industry", "status"],
    compare: true,
    granularity: true,
  },
  enquiry: {
    title: "Enquiries",
    icon: "mdi:file-document-outline",
    roles: SALES,
    View: EnquiriesView,
    description: "Requests from clients, and how many got a yes.",
    filters: ["ownerId", "stageId", "status", "priority", "type"],
    compare: true,
    granularity: true,
  },
  operation: {
    title: "Operations",
    icon: "mdi:cog-sync-outline",
    roles: ["ROLE_PROD-MANAGER"],
    View: OperationsView,
    description: "Production jobs: what is finished, waiting or failed.",
    filters: [],
  },
  completed: {
    title: "Completed jobs",
    icon: "mdi:check-all",
    roles: ["ROLE_PROD-MANAGER"],
    View: CompletedOperationsView,
    description:
      "Production jobs that are finished: what, who, when and for which order.",
    filters: [],
  },
  warehouse: {
    title: "Warehouse stock",
    icon: "mdi:warehouse",
    roles: ["ROLE_INV-MANAGER", "ROLE_INVENTORY-MANAGER", "ROLE_PROD-MANAGER"],
    View: WarehouseView,
    description:
      "How much stock you have, what came in and went out, and what is running low.",
    filters: [],
  },
};

const FILTERS = {
  ownerId: { label: "Handled by", options: "owners" },
  source: { label: "Source", options: "sources" },
  industry: { label: "Industry", options: "industries" },
  status: { label: "Status", options: "statuses" },
  stageId: { label: "Stage", options: "stages" },
  priority: { label: "Priority", options: "priorities" },
  type: { label: "Type", options: "types" },
};

const SEEDS = {
  lead: {
    statuses: ["AVAILABLE", "OFFLINE"].map((v) => ({ value: v, label: v })),
  },
  enquiry: {
    statuses: ["PENDING", "APPROVED", "REJECTED"].map((v) => ({
      value: v,
      label: v,
    })),
    priorities: ["HIGH", "MEDIUM", "LOW"].map((v) => ({ value: v, label: v })),
  },
};

const Reports = ({ reportType: reportTypeProp }) => {
  const { reportType: routeType } = useParams();
  const { hasRole } = useAuth();

  const menu = useMemo(
    () =>
      Object.entries(REPORTS).filter(([, r]) =>
        r.roles.some((role) => hasRole(role)),
      ),
    [hasRole],
  );
  const requested = reportTypeProp || routeType;
  const reportType = REPORTS[requested] ? requested : menu[0]?.[0];
  const config = REPORTS[reportType];
  const allowed = menu.some(([key]) => key === reportType);

  const [period, setPeriod] = useState(REPORT_PERIODS.MTD);
  const [dates, setDates] = useState(() => getPeriodDates(REPORT_PERIODS.MTD));
  const [compare, setCompare] = useState("PREVIOUS_PERIOD");
  const [granularity, setGranularity] = useState("AUTO");
  const [filters, setFilters] = useState({});
  const [options, setOptions] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState("charts");
  const pageRef = useRef(null);

  useEffect(() => {
    setFilters({});
    setOptions({});
  }, [reportType]);

  const registerOptions = useCallback((payload) => {
    setOptions((prev) => {
      const next = collectOptions(payload, prev);
      return Object.keys(next).every(
        (k) => next[k].length === (prev[k] || []).length,
      )
        ? prev
        : next;
    });
  }, []);

  const setFilter = useCallback((key, value) => {
    setFilters((cur) => {
      const next = { ...cur };
      if (!value || cur[key] === String(value)) delete next[key];
      else next[key] = String(value);
      return next;
    });
  }, []);

  const params = useMemo(
    () => ({
      ...dates,
      ...(config?.compare ? { compare } : {}),
      ...(config?.granularity ? { granularity } : {}),
      ...filters,
    }),
    [dates, compare, granularity, filters, config],
  );

  const periodText = describePeriod(period, dates);
  const compareWord = describeCompare(compare);
  const contextValue = useMemo(
    () => ({ view, periodText, compareWord }),
    [view, periodText, compareWord],
  );

  const handlePeriodChange = (next) => {
    setPeriod(next);
    if (next !== REPORT_PERIODS.CUSTOM) setDates(getPeriodDates(next));
  };
  const handleCustomChange = (key, value) => {
    const next = { ...dates, [key]: value };
    if (next.startDate && next.endDate && next.startDate <= next.endDate)
      setDates(next);
  };

  if (!menu.length) {
    return (
      <div className="reports-page">
        <div className="rp-empty-page">
          <Icon icon="mdi:lock-outline" />
          <h2>No reports available</h2>
          <p>
            Your role does not have access to any reports. Ask an administrator
            to update your access.
          </p>
        </div>
      </div>
    );
  }
  if (!allowed || requested !== reportType)
    return <Navigate to={`/reports/${reportType}`} replace />;

  const View = config.View;
  const optionsFor = (key) => {
    const meta = FILTERS[key];
    const list = [
      ...(SEEDS[reportType]?.[meta.options] || []),
      ...(options[meta.options] || []),
    ];
    const seen = new Map(list.map((o) => [o.value, o]));
    if (filters[key] && !seen.has(filters[key]))
      seen.set(filters[key], { value: filters[key], label: filters[key] });
    return [...seen.values()];
  };
  const activeFilters = Object.entries(filters).filter(([k]) => FILTERS[k]);
  const labelFor = (key, value) =>
    optionsFor(key).find((o) => o.value === value)?.label || value;

  return (
    <div className="reports-page" ref={pageRef}>
      <header className="rp-hero">
        <div className="rp-hero-icon">
          <Icon icon={config.icon} />
        </div>
        <div className="rp-hero-copy">
          <h1>{config.title}</h1>
          <p>{config.description}</p>
        </div>
        <div className="rp-hero-side">
          <span className="rp-range">
            <Icon icon="mdi:calendar-range" />
            {formatDate(dates.startDate)} to {formatDate(dates.endDate)}
          </span>
          <button
            type="button"
            className="rp-btn"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            <Icon icon="mdi:refresh" /> Refresh
          </button>
          <button
            type="button"
            className="rp-btn"
            onClick={() =>
              printElement(pageRef.current, `${config.title} report`)
            }
          >
            <Icon icon="mdi:printer-outline" /> Print
          </button>
        </div>
      </header>

      <div className="rp-navbar">
        <nav className="rp-tabs" aria-label="Reports">
          {menu.map(([key, r]) => (
            <Link
              key={key}
              to={`/reports/${key}`}
              className={key === reportType ? "active" : ""}
            >
              <Icon icon={r.icon} /> {r.title}
            </Link>
          ))}
        </nav>
        <div
          className="rp-viewswitch"
          role="tablist"
          aria-label="How to show this report"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === "charts"}
            className={view === "charts" ? "active" : ""}
            onClick={() => setView("charts")}
          >
            <Icon icon="mdi:chart-areaspline" /> Charts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "table"}
            className={view === "table" ? "active" : ""}
            onClick={() => setView("table")}
          >
            <Icon icon="mdi:table" /> Table
          </button>
        </div>
      </div>

      <section className="rp-toolbar">
        <div className="rp-toolbar-row">
          <ReportPeriodFilter
            period={period}
            dates={dates}
            onPeriodChange={handlePeriodChange}
            onCustomChange={handleCustomChange}
          />
          <div className="rp-toolbar-end">
            {config.compare && (
              <label className="rp-select">
                <span>Compare with</span>
                <select
                  value={compare}
                  onChange={(e) => setCompare(e.target.value)}
                >
                  <option value="PREVIOUS_PERIOD">The time before</option>
                  <option value="PREVIOUS_YEAR">Same time last year</option>
                  <option value="NONE">Nothing</option>
                </select>
              </label>
            )}
            {config.granularity && (
              <label className="rp-select">
                <span>Show by</span>
                <select
                  value={granularity}
                  onChange={(e) => setGranularity(e.target.value)}
                >
                  <option value="AUTO">Auto</option>
                  <option value="DAY">Day</option>
                  <option value="WEEK">Week</option>
                  <option value="MONTH">Month</option>
                </select>
              </label>
            )}
          </div>
        </div>
        {!!config.filters.length && (
          <div className="rp-filters">
            {config.filters.map((key) => (
              <label className="rp-select" key={key}>
                <span>{FILTERS[key].label}</span>
                <select
                  value={filters[key] || ""}
                  onChange={(e) => setFilter(key, e.target.value)}
                >
                  <option value="">All</option>
                  {optionsFor(key).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            {!!activeFilters.length && (
              <div className="rp-chips">
                {activeFilters.map(([k, v]) => (
                  <button
                    type="button"
                    className="rp-chip"
                    key={k}
                    onClick={() => setFilter(k, "")}
                    title="Remove filter"
                  >
                    {FILTERS[k].label}: {labelFor(k, v)}{" "}
                    <Icon icon="mdi:close" />
                  </button>
                ))}
                <button
                  type="button"
                  className="rp-link"
                  onClick={() => setFilters({})}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      <ReportContext.Provider value={contextValue}>
        <View
          key={reportType}
          params={params}
          filters={filters}
          setFilter={setFilter}
          onLoaded={registerOptions}
          refreshKey={refreshKey}
        />
      </ReportContext.Provider>
    </div>
  );
};

export default Reports;
